import { ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export interface EditorSectionProps {
  id: string;
  title: string;
  description?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  value?: string; // Added for controlled mode
  onValueChange?: (value: string | undefined) => void; // Added for controlled mode
  children: ReactNode;
}

export function EditorSection({
  id,
  title,
  description,
  collapsible = false,
  defaultCollapsed = false,
  value,
  onValueChange,
  children,
}: EditorSectionProps) {
  // Non-collapsible rendering
  if (!collapsible) {
    return (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium">{title}</h4>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {children}
      </div>
    );
  }

  // Use controlled mode if onValueChange is provided, otherwise use uncontrolled mode
  const handleValueChange = onValueChange
    ? (newValue: string) => onValueChange(newValue === id ? id : undefined)
    : undefined;

  // In controlled mode, use the provided value (can be undefined for collapsed)
  // In uncontrolled mode, use defaultCollapsed to determine initial state
  const accordionProps = onValueChange
    ? { value: value, onValueChange: handleValueChange }
    : { defaultValue: defaultCollapsed ? undefined : id };

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      {...accordionProps}
    >
      <AccordionItem value={id} className="border-none">
        <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
          {title}
        </AccordionTrigger>
        <AccordionContent className="space-y-3 pt-2">
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
